import * as Sentry from '@sentry/react';

const sentryDsn =
  process.env.REACT_APP_SENTRY_DSN ??
  process.env.VITE_SENTRY_DSN ??
  process.env.SENTRY_DSN ??
  '';

const sentryEnvironment =
  process.env.REACT_APP_SENTRY_ENVIRONMENT ??
  process.env.VITE_SENTRY_ENVIRONMENT ??
  process.env.SENTRY_ENVIRONMENT ??
  process.env.NODE_ENV;

const sentryRelease =
  process.env.REACT_APP_SENTRY_RELEASE ??
  process.env.VITE_SENTRY_RELEASE ??
  process.env.SENTRY_RELEASE;

const tracesSampleRate = parseSampleRate(
  process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE ??
    process.env.VITE_SENTRY_TRACES_SAMPLE_RATE ??
    process.env.SENTRY_TRACES_SAMPLE_RATE,
  0.1
);

const replaysSessionSampleRate = parseSampleRate(
  process.env.REACT_APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE ??
    process.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE ??
    process.env.SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
  0
);

const replaysOnErrorSampleRate = parseSampleRate(
  process.env.REACT_APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE ??
    process.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE ??
    process.env.SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
  1
);

const sentryEnabled =
  (process.env.REACT_APP_SENTRY_ENABLED ??
    process.env.VITE_SENTRY_ENABLED ??
    process.env.SENTRY_ENABLED ??
    'true')
    .toString()
    .toLowerCase() !== 'false';

let isConfigured = false;

function parseSampleRate(value: string | undefined, fallback: number): number {
  if (!value) {
    return fallback;
  }

  const parsed = Number(value);

  if (!Number.isFinite(parsed)) {
    return fallback;
  }

  if (parsed < 0) {
    return 0;
  }

  if (parsed > 1) {
    return 1;
  }

  return parsed;
}

export interface SentryBootstrapResult {
  didInit: boolean;
}

export function initSentry(): SentryBootstrapResult {
  if (isConfigured || !shouldEnableSentry()) {
    return { didInit: false };
  }

  const integrations = [Sentry.browserTracingIntegration()];

  if (typeof Sentry.replayIntegration === 'function') {
    integrations.push(
      Sentry.replayIntegration({
        blockAllMedia: true,
      })
    );
  }

  Sentry.init({
    dsn: sentryDsn,
    environment: sentryEnvironment,
    release: sentryRelease,
    integrations,
    tracesSampleRate,
    replaysSessionSampleRate,
    replaysOnErrorSampleRate,
    autoSessionTracking: false,
  });

  isConfigured = true;

  return { didInit: true };
}

function shouldEnableSentry(): boolean {
  if (!sentryEnabled) {
    return false;
  }

  if (!sentryDsn) {
    return false;
  }

  if (process.env.NODE_ENV === 'test') {
    return false;
  }

  return true;
}

// Exposed for testing to reset module state between suites.
export function __resetSentryForTests() {
  isConfigured = false;
}

export function isSentryReady(): boolean {
  return shouldEnableSentry();
}

export function hasInitializedSentry(): boolean {
  return isConfigured;
}

export { Sentry };
